import React from 'react';
import { Navigate, Link } from 'react-router-dom';
import { Box, Typography, Button, Avatar } from '@mui/material';
import { usePermissions } from '../contexts/PermissionsContext';
import HomeIcon from '@mui/icons-material/Home';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PersonIcon from '@mui/icons-material/Person';

export default function ProtectedRoute({ children, permission, anyPermissions, allPermissions }) {
  const { hasPermission, hasAnyPermission, hasAllPermissions, loading } = usePermissions();

  // Check if user is authenticated
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  if (!token) {
    return <Navigate to="/login" />;
  }

  // Wait for permissions to load
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#8C7361' }}>
        <Box sx={{ animation: 'pulse 1.5s infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.5 } } }}>
          <Typography variant="h5" fontWeight="900" color="white">جاري التحقق...</Typography>
        </Box>
      </Box>
    );
  }

  // Check permissions
  let hasAccess = true;

  if (permission) {
    hasAccess = hasPermission(permission);
  } else if (anyPermissions) {
    hasAccess = hasAnyPermission(anyPermissions);
  } else if (allPermissions) {
    hasAccess = hasAllPermissions(allPermissions);
  }

  if (!hasAccess) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          bgcolor: '#8C7361',
          fontFamily: '"Noto Sans Arabic", sans-serif'
        }}
      >
        {/* Logo Header */}
        <Box sx={{ mb: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <Avatar
            src="/st.png"
            sx={{ width: 80, height: 80, borderRadius: '20px', border: '4px solid white', boxShadow: '0 10px 15px rgba(0,0,0,0.1)', bgcolor: 'white' }}
          />
          <Typography variant="h4" sx={{ fontWeight: 900, color: 'white', mt: 2 }}>
            ST Studio
          </Typography>
        </Box>

        {/* Error Card */}
        <Box
          sx={{
            width: '100%',
            maxWidth: 512,
            bgcolor: 'white',
            borderRadius: '2.5rem',
            p: { xs: 5, md: 8 },
            textAlign: 'center',
            position: 'relative',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.15)',
            overflow: 'hidden'
          }}
        >
          {/* 403 Badge */}
          <Box sx={{ position: 'absolute', top: 24, left: 24 }}>
            <Box sx={{ px: 1.5, py: 0.5, bgcolor: '#F8FAFC', borderRadius: 99, border: '1px solid rgba(0,0,0,0.05)' }}>
              <Typography sx={{ fontSize: 10, fontWeight: 900, color: '#94A3B8', letterSpacing: 1 }}>403 ERROR</Typography>
            </Box>
          </Box>

          {/* Illustration replacement */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
            <Box sx={{ p: 3, bgcolor: '#F5F2ED', borderRadius: '50%', color: '#8C7361' }}>
              <PersonIcon sx={{ fontSize: 80 }} />
            </Box>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" sx={{ fontWeight: 900, color: '#4A3E37', mb: 2 }}>ليس لديك صلاحية للوصول</Typography>
            <Typography variant="body1" sx={{ color: '#8C7361', fontSize: '1.1rem', lineHeight: 1.6, maxWidth: 320, mx: 'auto', fontWeight: 600 }}>
              عذراً، ليس لديك الصلاحيات المطلوبة لعرض هذه الصفحة.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              component={Link}
              to="/dashboard"
              variant="contained"
              startIcon={<HomeIcon />}
              sx={{
                bgcolor: '#4A3E37',
                color: 'white',
                borderRadius: '1.2rem',
                py: 2,
                fontWeight: 800,
                fontSize: '1.1rem',
                textTransform: 'none',
                boxShadow: '0 10px 15px -3px rgba(74, 62, 55, 0.3)',
                '&:hover': { bgcolor: '#2D2521', transform: 'translateY(-2px)' },
                transition: 'all 0.2s'
              }}
            >
              العودة للرئيسية
            </Button>
            <Button
              variant="text"
              startIcon={<HelpOutlineIcon />}
              sx={{
                color: '#8C7361',
                py: 1.5,
                fontWeight: 700,
                '&:hover': { bgcolor: '#F9F8F6' }
              }}
            >
              اتصل بالدعم الفني
            </Button>
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ mt: 8, display: 'flex', gap: 4 }}>
          {['عن ST Studio', 'سياسة الخصوصية', 'الدعم الفني'].map(link => (
            <Typography key={link} sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.875rem', fontWeight: 800, cursor: 'pointer', '&:hover': { color: 'white' } }}>
              {link}
            </Typography>
          ))}
        </Box>
      </Box>
    );
  }

  return children;
}